import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { tryRefreshToken, redirectToLogin, getTokenExpMs } from '../../api/base';

const IDLE_TIMEOUT_MS = 10 * 60 * 1000;
const COUNTDOWN_SECONDS = 5 * 60;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
const THROTTLE_MS = 1000;
const STORAGE_KEY = 'lastActivity';
const TOKEN_REFRESH_BUFFER_MS = 3 * 60 * 1000;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const IdleTimeoutDialog = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastThrottleRef = useRef(0);
  const showDialogRef = useRef(false);
  const stayLoggedInRef = useRef<() => void>(() => {});

  useEffect(() => {
    function clearIdleTimer() {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    }

    function clearCountdown() {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }

    function clearRefreshTimer() {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }

    function scheduleTokenRefresh() {
      clearRefreshTimer();
      const expMs = getTokenExpMs();
      if (!expMs) return;

      const delay = expMs - Date.now() - TOKEN_REFRESH_BUFFER_MS;
      if (delay <= 0) {
        tryRefreshToken().then(ok => {
          if (ok) scheduleTokenRefresh();
        });
        return;
      }

      refreshTimerRef.current = setTimeout(() => {
        tryRefreshToken().then(ok => {
          if (ok) scheduleTokenRefresh();
        });
      }, delay);
    }

    function startCountdown() {
      setCountdown(COUNTDOWN_SECONDS);
      setShowDialog(true);
      showDialogRef.current = true;

      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current!);
            countdownRef.current = null;
            redirectToLogin();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    function startIdleTimer() {
      clearIdleTimer();
      idleTimerRef.current = setTimeout(() => {
        const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
        const elapsed = Date.now() - lastActivity;
        const CROSS_TAB_BUFFER_MS = 5000;

        if (elapsed < IDLE_TIMEOUT_MS - CROSS_TAB_BUFFER_MS) {
          startIdleTimer();
          return;
        }

        startCountdown();
      }, IDLE_TIMEOUT_MS);
    }

    function recordActivity() {
      const now = Date.now();
      if (now - lastThrottleRef.current < THROTTLE_MS) return;
      lastThrottleRef.current = now;
      localStorage.setItem(STORAGE_KEY, String(now));

      if (!showDialogRef.current) {
        startIdleTimer();
      }
    }

    stayLoggedInRef.current = async () => {
      clearCountdown();
      setShowDialog(false);
      showDialogRef.current = false;

      const ok = await tryRefreshToken();
      if (!ok) {
        redirectToLogin();
        return;
      }

      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      startIdleTimer();
      scheduleTokenRefresh();
    };

    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    startIdleTimer();
    scheduleTokenRefresh();

    ACTIVITY_EVENTS.forEach(evt =>
      document.addEventListener(evt, recordActivity, { passive: true })
    );

    return () => {
      clearIdleTimer();
      clearCountdown();
      clearRefreshTimer();
      ACTIVITY_EVENTS.forEach(evt =>
        document.removeEventListener(evt, recordActivity)
      );
    };
  }, []);

  return (
    <Dialog
      open={showDialog}
      disableEscapeKeyDown
      onClose={(_e, reason) => {
        if (reason === 'backdropClick') return;
      }}
    >
      <DialogTitle>Session Timeout Warning</DialogTitle>
      <DialogContent>
        <Typography>
          You've been inactive. Your session will expire in{' '}
          <strong>{formatTime(countdown)}</strong>.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => stayLoggedInRef.current()}>
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutDialog;
